import React from "react";
import { Card, Typography, Box, LinkButton } from "@bca/ui-library";

import SpannerSVG from "./Icons/SpannerSVG";
import { Report } from "../vehicleDetails/vehicle-details-props.type";
import { MechanicalReportAnalytics } from "../analytics/MechanicalReportAnalytics";
import pushToDigitalData from "../analytics/pushToDigitalData";
import { shallowEqual, useSelector } from "react-redux";
import { StoreType } from "../redux/StoreType";
import { GET_USER_STATE } from "../redux/slices/userSlice";
import { GET_VENDOR_IMPERSONATION_STATE } from "../redux/slices/impersonationSlice";

const reportDetails = {
  Standard: {
    title: "Assured Report",
    text: "This 45+ point report includes detailed mechanical assessments, providing a comprehensive mechanical overview of the areas listed, including: engine bay, engine running, dynamic operation, interior, tyre tread, with commentary and notes where relevant. These reports include an Essential Checks section. The checks in this section are provided for information and are not eligible to be claimed on.",
    linkText: "View Assured Report",
  },
  ElectricHybrid: {
    title: "Assured EV/Hybrid Report",
    text: "This 45+ point report is designed to give you specific information on EV / Hybrid vehicles in addition to the standard BCA Assured checks. Detailed mechanical assessments provided include: Traction pack diagnostics, inverter coolant level, charging status, charge port condition, with commentary and notes where relevant. These reports include an Essential Checks section. The checks in this section are provided for information and are not eligible to be claimed on.",
    linkText: "View Assured Report",
  },
  FullInspection: {
    title: "128 Vehicle Inspection",
    text: "This enhanced inspection provides a detailed assessment of 128 areas on the vehicle.\n Areas include: engine compartment, transmission, suspension, steering, fuel system, tyres, electrical controls,  exhaust system, brakes, a road test and an inspector's summary of any faults found (where applicable).\nVehicles are sold As Is and no claims will be accepted on the information provided on this report.",
    linkText: "View Mechanical Report",
  },
  EssentialChecks: {
    title: "BCA Essential Check",
    text: "Carried out by trained inspectors this is a 35+ point check on older, higher mileage vehicles. Vehicles are sold as seen and no claims will be accepted.",
    linkText: "View Essential Report",
  },
  NoReport: {
    title: "NoReport",
    text: "NoReport",
    linkText: "NoReport",
  },
};

interface MechanicalReportDetailsProps {
  report: Report;
  conditionSummaries?: ConditionSummaries[];
}

interface ConditionSummaries {
  severityType: string;
  numberOfItems: number;
  reportType: string;
}

const MechanicalReportDetails: React.FunctionComponent<
  MechanicalReportDetailsProps
> = ({ report: { type, href }, conditionSummaries }) => {
  const cardCss = `
      display: flex;
      flex-direction: column;
    `;
  const titleCss = "margin: 0";
  const linkCss = `
    justify-content: flex-start;
    height: auto;
    `;

  let data = conditionSummaries ?? [];
  let filterReport = data.filter((item) => item.reportType === type);

  const result = MechanicalReportAnalytics(type, filterReport);
  let analyticsItemData = result?.toString();

  const {
    user: { isVendorAdmin },
  }: StoreType = useSelector(GET_USER_STATE, shallowEqual);
  const {
    impersonation: { isVendorImpersonationSet },
  }: StoreType = useSelector(GET_VENDOR_IMPERSONATION_STATE, shallowEqual);

  const pushAnalytics = (
    isVendorAdmin: boolean,
    isVendorImpersonationSet: boolean
  ) => {
    if (
      (isVendorAdmin && isVendorImpersonationSet) ||
      isVendorAdmin === false
    ) {
      pushToDigitalData({
        event: {
          name: "click",
          category: "button",
          label: analyticsItemData,
          action: "download",
          section: "secondary-information",
        },
      });
    }
  };

  return (
    <Card
      width={["100%", "49%"]}
      height="auto"
      marginX={0}
      marginY={[3, 0, 0]}
      css={cardCss}
      data-testid="mechanical-report-card"
    >
      <Box display="flex" justifyContent="space-between">
        <Typography variant="h5" css={titleCss}>
          {reportDetails[type].title}
        </Typography>
        <SpannerSVG mr={12} maxWidth={[31, 30]} height={[30]}></SpannerSVG>
      </Box>
      <Box
        display="flex"
        flex={1}
        flexDirection="column"
        justifyContent="space-between"
      >
        {reportDetails[type].text.split("\n").map((line, index) => (
          <Typography variant="body" color="grey-blue" key={index}>
            {line}
            <br />
          </Typography>
        ))}
        <LinkButton
          link={href}
          text={reportDetails[type].linkText}
          shouldOpenInNewWindow
          icon="arrow-right-solid"
          iconAlignment="right"
          variant="tertiary"
          paddingX={0}
          css={linkCss}
          onClick={() => {
            pushAnalytics(isVendorAdmin, isVendorImpersonationSet);
          }}
        ></LinkButton>
      </Box>
    </Card>
  );
};

export default MechanicalReportDetails;
